import type { BoardEvent, BoardRouteEdge } from "../../../../../../../_logic/calendar-types"
import { ROUTE_VISUAL_GAP_PX } from "../_shared/_logic/route-constants"

const SAME_VERTICAL_STACK_THRESHOLD_PCT = 2.5

type ApplyRouteVisualGapsInput = {
  events: BoardEvent[]
  edges?: BoardRouteEdge[] | null
  minGapPx?: number
}

const eventBottom = (item: BoardEvent) => (item.top ?? 0) + (item.height ?? 0)

const eventCenterPct = (item: BoardEvent) => {
  if (item.event.flex_type === "SOFT") return 0

  return (item.laneLeftPct ?? 0) + (item.laneWidthPct ?? 100) / 2
}

const isSameVerticalStack = (previous: BoardEvent, next: BoardEvent) =>
  Math.abs(eventCenterPct(previous) - eventCenterPct(next)) <= SAME_VERTICAL_STACK_THRESHOLD_PCT

export const applyRouteVisualGaps = ({
  events,
  edges,
  minGapPx = ROUTE_VISUAL_GAP_PX,
}: ApplyRouteVisualGapsInput): BoardEvent[] => {
  const byId = new Map(events.map((item) => [String(item.event.id), { ...item }]))

  for (const edge of edges ?? []) {
    const previous = byId.get(edge.previousEventId)
    const next = byId.get(edge.nextEventId)

    if (!previous || !next) continue

    if (!isSameVerticalStack(previous, next)) continue

    const visualGap = (next.top ?? 0) - eventBottom(previous)

    if (visualGap >= minGapPx || visualGap < 0) continue

    const missingGap = minGapPx - visualGap
    const previousInset = Math.min(
      Math.ceil(missingGap / 2),
      Math.max(0, ((previous.height ?? 0) - 18) / 2),
    )
    const nextInset = Math.min(
      Math.floor(missingGap / 2),
      Math.max(0, ((next.height ?? 0) - 18) / 2),
    )
    previous.routeInsetBottomPx = Math.max(previous.routeInsetBottomPx ?? 0, previousInset)
    next.routeInsetTopPx = Math.max(next.routeInsetTopPx ?? 0, nextInset)
  }

  return events.map((item) => byId.get(String(item.event.id)) ?? item)
}
